import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { contactApi } from '../services';
import icpApi from '../services/icpApi';
import useAutocomplete from '../hooks/useAutocomplete';
import AutocompleteInput from './AutocompleteInput';
import Icon from './Icon';
import './RelationshipManagers.css';
import './ConferencePrepPanel.css';

const TIERS = ['S', 'A', 'B', 'C'];

/**
 * Add an existing company to the pipeline, with existing contacts there as who to reach - the
 * Kanban board's "+" button. Mirrors ConferencePrepPanel's search-then-act shape; unlike that
 * panel this one writes (POST /target-accounts), so it stays open on an error instead of closing.
 */
const AddTargetAccountDialog = ({ isOpen, onClose, onCreated }) => {
  const [company, setCompany] = useState(null);
  const [segments, setSegments] = useState(null);
  const [icpId, setIcpId] = useState('');
  const [tier, setTier] = useState('');
  const [contacts, setContacts] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [mainContactId, setMainContactId] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const dialogRef = useRef(null);
  const lookupRef = useRef(0);

  const autocomplete = useAutocomplete('company', {
    onSelect: async (picked) => {
      const lookup = ++lookupRef.current;
      setCompany(picked);
      setContacts(null);
      setSelectedIds([]);
      setMainContactId(null);
      setError(null);
      try {
        const data = await contactApi.getCompanyContacts(picked.id);
        if (lookup === lookupRef.current) setContacts(data.contacts || []);
      } catch (err) {
        if (lookup === lookupRef.current) setError(err.message);
      }
    }
  });
  const { clear } = autocomplete;

  useEffect(() => {
    if (!isOpen) return;
    lookupRef.current += 1;
    setCompany(null);
    setContacts(null);
    setSelectedIds([]);
    setMainContactId(null);
    setIcpId('');
    setTier('');
    setError(null);
    clear();
    dialogRef.current?.focus();
    icpApi.listSegments().then(data => setSegments(data.segments || [])).catch(() => setSegments([]));
  }, [isOpen, clear]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || dialogRef.current?.querySelector('.autocomplete-suggestion')) return;
      event.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const toggleContact = (contactId) => {
    setSelectedIds(current => (current.includes(contactId)
      ? current.filter(id => id !== contactId)
      : [...current, contactId]));
    setMainContactId(current => current ?? contactId);
  };

  const canSubmit = company && icpId && selectedIds.length >= 0 && !submitting;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const created = await contactApi.createTargetAccount({
        company_id: company.id,
        icp_id: Number(icpId),
        tier: tier || null,
        contacts: selectedIds.map(id => ({ contact_id: id, role: id === mainContactId ? 'to' : 'cc' })),
      });
      onCreated(created);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <>
      <div className="rm-overlay" onClick={onClose} aria-hidden="true" />
      <div className="rm-dialog" role="dialog" aria-modal="true" aria-labelledby="add-target-account-title"
           ref={dialogRef} tabIndex={-1}>
        <header className="rm-dialog__header">
          <h2 id="add-target-account-title"><Icon name="plus" /> Add to pipeline</h2>
          <button type="button" className="rm-icon-button" onClick={onClose} aria-label="Close">
            <Icon name="x" />
          </button>
        </header>

        <div className="rm-dialog__toolbar">
          <AutocompleteInput
            value={autocomplete.query}
            onChange={autocomplete.setQuery}
            onSelect={autocomplete.handleSelect}
            suggestions={autocomplete.suggestions}
            loading={autocomplete.loading}
            placeholder="Search for a company..."
            showLogos
            itemType="company"
          />
        </div>

        <div className="rm-dialog__body">
          {!company && (
            <p className="placeholder-text">Search an existing company to add it to a target list.</p>
          )}
          {error && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}

          {company && (
            <>
              <h3 className="cp-company__name">{company.name}</h3>

              <label className="rm-field">
                <span>Target list</span>
                <select className="rm-input" value={icpId} onChange={(event) => setIcpId(event.target.value)}>
                  <option value="">Choose a list...</option>
                  {(segments || []).map(segment => (
                    <option key={segment.id} value={segment.id}>{segment.name}</option>
                  ))}
                </select>
              </label>

              <label className="rm-field">
                <span>Tier (optional)</span>
                <select className="rm-input" value={tier} onChange={(event) => setTier(event.target.value)}>
                  <option value="">Not set</option>
                  {TIERS.map(value => <option key={value} value={value}>{value}</option>)}
                </select>
              </label>

              <div className="rm-field">
                <span>People to reach (optional)</span>
                {contacts === null && <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading contacts...</p>}
                {contacts && contacts.length === 0 && (
                  <p className="rm-empty">No contacts on file at {company.name} yet.</p>
                )}
                {contacts && contacts.length > 0 && (
                  <ul className="cp-people">
                    {contacts.map(contact => (
                      <li className="cp-person" key={contact.id}>
                        <label className="rm-checkbox-row">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(contact.id)}
                            onChange={() => toggleContact(contact.id)}
                          />
                          <span className="cp-person__name">{contact.name}</span>
                          {contact.title && <span className="cp-person__title">{contact.title}</span>}
                        </label>
                        {selectedIds.includes(contact.id) && (
                          <button
                            type="button"
                            className="rm-button rm-button--quiet"
                            disabled={mainContactId === contact.id}
                            onClick={() => setMainContactId(contact.id)}
                          >
                            {mainContactId === contact.id ? 'Main contact' : 'Set as main contact'}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <button type="button" className="rm-button rm-button--primary" disabled={!canSubmit} onClick={handleSubmit}>
                {submitting ? <><Icon name="loader" spin /> Adding...</> : 'Add to pipeline'}
              </button>
            </>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

export default AddTargetAccountDialog;
